import { createStore } from '@xstate/store';
import type { Employee } from '../types';

// ============================================
// Employees Store - Verwaltet die Mitarbeiter
// ============================================

const EMPLOYEES_LOCAL_STORAGE_KEY = 'roadmap_2026_employees';
const PERSISTENCE_DEBOUNCE_MS = 500;

export interface EmployeesContext {
  employees: Employee[];
}

// Lade Employees aus LocalStorage
function loadEmployeesFromLocalStorage(): Employee[] {
  try {
    const saved = localStorage.getItem(EMPLOYEES_LOCAL_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved) as Employee[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch {
    return [];
  }
}

// Speichere Employees in LocalStorage
function saveEmployeesToLocalStorage(employees: Employee[]): void {
  localStorage.setItem(EMPLOYEES_LOCAL_STORAGE_KEY, JSON.stringify(employees));
}

const initialContext: EmployeesContext = {
  employees: loadEmployeesFromLocalStorage(),
};

export const employeesStore = createStore({
  context: initialContext,

  on: {
    'employee.add': (ctx, event: { employee: Employee }) => {
      // Prüfe ob ID bereits existiert
      if (ctx.employees.some(e => e.id === event.employee.id)) {
        console.warn(`[employeesStore] Employee with ID "${event.employee.id}" already exists`);
        return ctx;
      }
      return {
        ...ctx,
        employees: [...ctx.employees, event.employee],
      };
    },

    'employee.update': (ctx, event: { employeeId: string; updates: Partial<Omit<Employee, 'id'>> }) => {
      const employeeIndex = ctx.employees.findIndex(e => e.id === event.employeeId);
      if (employeeIndex === -1) {
        console.warn(`[employeesStore] Employee with ID "${event.employeeId}" not found`);
        return ctx;
      }

      const updatedEmployees = [...ctx.employees];
      updatedEmployees[employeeIndex] = {
        ...updatedEmployees[employeeIndex],
        ...event.updates,
      };

      return {
        ...ctx,
        employees: updatedEmployees,
      };
    },

    'employee.delete': (ctx, event: { employeeId: string }) => {
      const employeeIndex = ctx.employees.findIndex(e => e.id === event.employeeId);
      if (employeeIndex === -1) {
        console.warn(`[employeesStore] Employee with ID "${event.employeeId}" not found`);
        return ctx;
      }
      return {
        ...ctx,
        employees: ctx.employees.filter(e => e.id !== event.employeeId),
      };
    },

    'employees.reset': () => ({
      employees: [],
    }),

    'employees.import': (ctx, event: { employees: Employee[] }) => ({
      ...ctx,
      employees: event.employees,
    }),
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastEmployeesHash = '';

function computeEmployeesHash(employees: Employee[]): string {
  return employees.map(e => `${e.id}:${e.name}:${e.team}:${e.color}`).join('|');
}

// Subscribe to changes and persist
employeesStore.subscribe((snapshot) => {
  const currentHash = computeEmployeesHash(snapshot.context.employees);

  if (currentHash === lastEmployeesHash) return;
  lastEmployeesHash = currentHash;

  // Debounced persistence
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveEmployeesToLocalStorage(snapshot.context.employees);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// ============================================
// Helper-Funktionen
// ============================================

export function getEmployeesArray(): Employee[] {
  return employeesStore.getSnapshot().context.employees;
}

export function getEmployeeById(employeeId: string): Employee | undefined {
  return employeesStore.getSnapshot().context.employees.find(e => e.id === employeeId);
}

// Generiert eine neue eindeutige Employee-ID
export function generateEmployeeId(baseName: string): string {
  const baseId = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const existingEmployees = getEmployeesArray();
  let id = baseId || 'new_employee';
  let counter = 1;

  while (existingEmployees.some(e => e.id === id)) {
    id = `${baseId}_${counter}`;
    counter++;
  }

  return id;
}

// Vordefinierte Farben für Mitarbeiter
export const EMPLOYEE_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#6366f1', // indigo
  '#84cc16', // lime
  '#a855f7', // purple
  '#06b6d4', // cyan
  '#ea580c', // orange-dark
];

export type EmployeesStore = typeof employeesStore;
